import { catchError, map, tap } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { ConsumerPortalApiService } from './consumer-portal-api.service';
import { Searchable } from '../models/searchable.model';
import { ContractConversionDto, ContractCP, ContractDetail, EsignStateModel, EsignUpdateResponse, LineItems, Payment } from '../models/contract-model';
import { EsignUpdateIntent } from '../enums/esign-state.enum';
import isEmpty from 'lodash-es/isEmpty';
import { FilterOutput } from '../shared/models/filters.model';
import { ProductDetail, ProductDetailSearchable } from '../models/product-detail';
import { CloneContract } from '../models/clone-contract.model';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { FormatCurrencyPipe } from '../pipes/format-currency.pipe';
import { TranslateService } from '@ngx-translate/core';
import { FetchContractsParams } from '../models/contracts.model';
import { AppFacadeService } from './app-facade.service';

type ContractDetailSearchable = Searchable & ContractDetail;
type ContractDetailLineItemsSearchable = Searchable & LineItems;
type ContractDetailpaymentSearchable = Searchable & Payment;
type EsignStateSearchable = Searchable & EsignStateModel;
@Injectable({
    providedIn: 'root'
})
export class ContractService {
    private readonly appfacadeService = inject(AppFacadeService);
    private readonly consumerPortalApi = inject(ConsumerPortalApiService);
    private readonly formatCurrency = inject(FormatCurrencyPipe);
    private readonly translateService = inject(TranslateService);

    loadContracts(params: FetchContractsParams): void {
        const { customerId, status = [], startDate, endDate, search } = params;
        this.appfacadeService.dispatchFetchContract({ customerId, status, startDate, endDate, search });
    }

    getContractsDetails(customerId: string, contractId: string, columns?: string[]) {
        const headers: { [key: string]: string } = {};
        const url = `customer/${customerId}/contracts/${contractId}/detail`;
        const defaultColumns = [
            'Id',
            'Name',
            'Identifiers',
            'CustomerId',
            'CustomerName',
            'ContractStatus',
            'Status',
            'DepotId',
            'EndDateTime',
            'StartDateTime',
            'ESignState',
            'EsignUrl',
            'GrandTaxTotal',
            'GrandTotal',
            'CreatedDateTime',
            'UpdatedDateTime',
            'AmountDue',
            'DriverDelivery',
            'DriverPickup',
            'Addresses',
            'PONumber',
            'AdditionalFields',
            'Site',
            'Totals'
        ];
        const allColumns = columns && columns.length > 0 ? [...columns, ...defaultColumns] : [...defaultColumns];
        headers['x-columns'] = JSON.stringify(allColumns);
        return this.consumerPortalApi.get<ContractDetailSearchable>(url, { headers: headers });
    }
    getContractsDetailsLineItems(customerId: string, contractId: string) {
        const headers: { [key: string]: string } = {};
        const url = `customer/${customerId}/contracts/${contractId}/detail/lineitems`;
        return this.consumerPortalApi.get<ContractDetailLineItemsSearchable>(url, { headers: headers });
    }
    getContractsDetailsPaymentHistory(customerId: string, contractId: string) {
        const headers: { [key: string]: string } = {};
        const url = `customer/${customerId}/contracts/${contractId}/detail/payment`;
        return this.consumerPortalApi.get<ContractDetailpaymentSearchable>(url, { headers: headers });
    }

    requestEsignUpdate(customerId: string, contractId: string, redirectUrl: string, intent: EsignUpdateIntent): Observable<EsignUpdateResponse> {
        const params = {
            controller: `customer/${customerId}/contracts/${contractId}/esign-request`,
            method: 'POST',
            body: { redirectUrl, intent }
        };
        // consumerPortalApi.post already camelCases the response; passing it through avoids a double transform that drops fields.
        return this.consumerPortalApi.post<EsignUpdateResponse>({ ...params });
    }

    // Note: the esign-state endpoint returns a single Elite record, not a collection — the api-lib
    // route never wraps it in an array — so unwrap it here rather than treating it as EsignStateSearchable[].
    getEsignState(customerId: string, contractId: string): Observable<EsignStateModel> {
        const url = `customer/${customerId}/contracts/${contractId}/esign-state`;
        return this.consumerPortalApi.get<EsignStateSearchable>(url, { headers: {} }).pipe(map(record => record as unknown as EsignStateModel));
    }

    getProductDetails(customerId: string, productId: string, stockId: string, columns?: string[]) {
        const url = `product/${customerId}/?productId=${productId}&stockId=${stockId}`;
        const headers: { [key: string]: string } = {};
        if (columns && columns.length > 0) {
            headers['x-columns'] = JSON.stringify(columns);
        }
        return this.consumerPortalApi.get<ProductDetailSearchable>(url, { headers: headers }).pipe(
            tap({
                next: (productDetail: ProductDetail[]) => {
                    return productDetail;
                }
            })
        );
    }

    /**
     * Fetches stock detail by AssetTagId for public (unauthenticated) access.
     * Calls existing product endpoint with assetTagId query param.
     * orgId sent as x-organization-id header (required by AssetTagPublicGuard on the backend).
     * Public asset tag access.
     */
    getPublicProductByAssetTag(orgId: string, assetTagId: string): Observable<ProductDetail[]> {
        const encodedAssetTagId = encodeURIComponent(assetTagId);
        const url = `product/0?productId=0&stockId=0&assetTagId=${encodedAssetTagId}`;
        /* eslint-disable @typescript-eslint/naming-convention */
        const headers = { 'x-organization-id': orgId };
        /* eslint-enable @typescript-eslint/naming-convention */
        return this.consumerPortalApi.get<ProductDetailSearchable>(url, { headers }).pipe(
            catchError((err: HttpErrorResponse) => {
                return throwError(() => err);
            })
        );
    }

    filter($e: FilterOutput, customerId: string) {
        const filters =
            $e.multifilters?.map((value: string) => {
                return value;
            }) || [];
        const startDate: string | undefined = !isEmpty($e.dates.startDate) ? $e.dates.startDate : undefined;

        const endDate: string | undefined = !isEmpty($e.dates.endDate) ? $e.dates.endDate : undefined;
        this.appfacadeService.dispatchResetPageNumberContract();
        this.appfacadeService.dispatchResetContractsLatestToShow();
        this.loadContracts({
            customerId,
            status: filters,
            startDate,
            endDate,
            search: $e.search
        });
    }

    cloneContract(cloneRequest: CloneContract) {
        const params = {
            controller: `customer/${cloneRequest.customerId}/clone-contract`,
            method: 'POST',
            body: cloneRequest
        };
        return this.consumerPortalApi.post({ ...params }).pipe(
            tap({
                next: () => {
                    return of(true);
                },
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                error: (err: HttpErrorResponse) => {
                    return of(false);
                }
            })
        );
    }

    getAmountDueText(amountDue: number | string, paymentPending: boolean | undefined = false): string {
        if (paymentPending) {
            return this.translateService.instant('Pending');
        } else {
            return this.formatCurrency.transform(String(amountDue));
        }
    }

    getContractTotalPaid(contract: ContractDetail | undefined): number {
        let totalPaid = 0;
        if (contract) {
            const grandTotal: number | undefined = Number(contract.GrandTotal);
            const amountDue: number | undefined = contract.AmountDue;
            totalPaid = grandTotal - amountDue;
        }
        return totalPaid;
    }

    convertContractDetailToContractCP(contractDetail: ContractDetail): ContractCP {
        /* eslint-disable @typescript-eslint/naming-convention */
        return {
            contractId: contractDetail.Id,
            contractName: contractDetail.Name,
            status: contractDetail.Status ?? contractDetail.ContractStatus,
            openDate: contractDetail.StartDateTime,
            closeDate: contractDetail.EndDateTime,
            purchaseOrder: contractDetail?.PONumber ? contractDetail?.PONumber : '',
            jobNumber: contractDetail.AdditionalFields ? contractDetail.AdditionalFields.CustomerJobNumber : '',
            info: contractDetail?.Site ? contractDetail?.Site : '',
            GrandTotal: contractDetail.GrandTotal ?? '0',
            AmountDue: String(contractDetail.AmountDue) ?? '0',
            depotId: contractDetail?.DepotId ?? '0',
            LineItems: contractDetail?.LineItems || [],
            customerId: contractDetail?.CustomerId ?? '0',
            itemsOut: false,
            paid: '',
            CurrencyCode: contractDetail?.CurrencyCode
        };
    }

    convertQuoteToReservation(customerId: string, contractId: string): Observable<ContractConversionDto> {
        const url = `customer/${customerId}/contracts/${contractId}/convert-to-reservation`;
        return this.consumerPortalApi.patchRequest<ContractConversionDto>({
            controller: url,
            method: 'PATCH',
            body: { contractId: contractId },
            camelCase: false
        });
    }
}
